import React, { useState, useEffect } from 'react';
import {
    Key, Plus, Pencil, Lock, ArrowLeft, Check, Shield, MoreVertical, PowerOff,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import LoadingScreen from '../components/ui/LoadingScreen';
import { showSuccess, showError } from '../components/Alerts';
import { roleService } from '../services/roleService';
import { RoleWithPermissions, Permission } from '../types';
import { useAuth } from '../context/AuthContext';
import { cn } from '../lib/utils';

// ── Display maps ───────────────────────────────────────────────────────────

type PermissionDisplayConfig = { title: string; detail: string };

const PERMISSION_DISPLAY_MAP: Record<string, PermissionDisplayConfig> = {
    'dashboard.ver': { title: 'Ver panel principal', detail: 'Permite acceder al tablero general con el resumen operativo del sistema.' },
    'dashboard.finanzas': { title: 'Ver indicadores financieros del panel', detail: 'Permite visualizar métricas e indicadores financieros dentro del panel principal.' },
    'dashboard.ventas': { title: 'Ver indicadores comerciales del panel', detail: 'Permite visualizar métricas e indicadores de ventas dentro del panel principal.' },
    'dashboard.compras': { title: 'Ver indicadores de compras del panel', detail: 'Permite visualizar métricas e indicadores del proceso de compras dentro del panel principal.' },
    'proyectos.ver': { title: 'Ver proyectos', detail: 'Permite consultar el listado y el detalle de proyectos registrados.' },
    'proyectos.crear': { title: 'Crear proyectos', detail: 'Permite dar de alta nuevos proyectos en el sistema.' },
    'proyectos.editar': { title: 'Editar proyectos', detail: 'Permite modificar la información de proyectos existentes.' },
    'proyectos.eliminar': { title: 'Eliminar proyectos', detail: 'Permite eliminar proyectos del sistema. Acción irreversible.' },
    'unidades.ver': { title: 'Ver unidades funcionales', detail: 'Permite acceder a la gestión y consulta de unidades funcionales.' },
    'unidades.editar': { title: 'Editar unidades funcionales', detail: 'Permite crear, modificar o eliminar unidades funcionales en los proyectos.' },
    'clientes.ver': { title: 'Ver clientes', detail: 'Permite consultar el listado y la información de clientes.' },
    'clientes.editar': { title: 'Gestionar clientes', detail: 'Permite crear y modificar datos de clientes.' },
    'precios.ver': { title: 'Ver listas de precios', detail: 'Permite consultar listas de precios y sus detalles.' },
    'precios.editar': { title: 'Editar listas de precios', detail: 'Permite actualizar valores y configuración de listas de precios.' },
    'ventas.ver': { title: 'Ver historial de ventas', detail: 'Permite consultar operaciones de ventas realizadas.' },
    'ventas.crear': { title: 'Registrar ventas', detail: 'Permite generar nuevas ventas en el sistema.' },
    'proveedores.ver': { title: 'Ver proveedores', detail: 'Permite consultar el listado y datos de proveedores.' },
    'proveedores.editar': { title: 'Gestionar proveedores', detail: 'Permite crear y modificar información de proveedores.' },
    'oc.ver': { title: 'Ver órdenes de compra', detail: 'Permite acceder al listado y detalle de órdenes de compra.' },
    'oc.crear': { title: 'Crear órdenes de compra', detail: 'Permite emitir nuevas órdenes de compra.' },
    'oc.editar': { title: 'Editar órdenes de compra', detail: 'Permite modificar o aprobar órdenes de compra existentes.' },
    'facturas_proveedor.ver': { title: 'Ver facturas de proveedores', detail: 'Permite consultar facturas recibidas de proveedores.' },
    'facturas_proveedor.editar': { title: 'Gestionar facturas de proveedores', detail: 'Permite cargar, actualizar y procesar facturas de proveedores.' },
    'cobranzas.ver': { title: 'Ver cobranzas', detail: 'Permite acceder al módulo de cobranzas para consulta.' },
    'cobranzas.operar': { title: 'Operar cobranzas', detail: 'Permite registrar y gestionar cobros de clientes.' },
    'cajas.ver': { title: 'Ver cajas', detail: 'Permite consultar saldos y movimientos de cajas.' },
    'cajas.operar': { title: 'Operar cajas', detail: 'Permite registrar ingresos, egresos y transferencias de cajas.' },
    'transacciones.ver': { title: 'Ver historial de transacciones', detail: 'Permite consultar el registro global de transacciones.' },
    'cuentas_corrientes.ver': { title: 'Gestionar cuentas corrientes de clientes', detail: 'Permite consultar y operar la cuenta corriente de clientes: ver movimientos, registrar ingresos, pagos y cargos.' },
    'reportes.ver': { title: 'Ver reportes contables y financieros', detail: 'Permite acceder a reportes de análisis contable y financiero.' },
    'contabilidad.ver': { title: 'Acceder al módulo de contabilidad', detail: 'Permite ingresar y consultar la pantalla de Contabilidad.' },
    'contabilidad.operar': { title: 'Operar en contabilidad', detail: 'Permite registrar pagos y ejecutar acciones operativas dentro de Contabilidad.' },
    'config.ver': { title: 'Ver configuración del sistema', detail: 'Permite consultar parámetros y catálogos de configuración.' },
    'config.editar': { title: 'Editar configuración del sistema', detail: 'Permite modificar parámetros y catálogos de configuración.' },
    'usuarios.gestionar': { title: 'Gestionar usuarios', detail: 'Permite crear, editar, activar o desactivar usuarios.' },
    'roles.ver': { title: 'Ver roles', detail: 'Permite consultar los perfiles de roles y sus permisos.' },
    'roles.gestionar': { title: 'Gestionar roles', detail: 'Permite crear y modificar la asignación de permisos por rol.' },
};

const MODULE_DISPLAY_MAP: Record<string, string> = {
    DASHBOARD: 'Panel Principal',
    PROYECTOS: 'Proyectos y Unidades',
    UNIDADES: 'Unidades Funcionales',
    COMERCIAL: 'Gestión Comercial',
    COMPRAS: 'Compras y Abastecimiento',
    CONTABILIDAD: 'Contabilidad y Finanzas',
    CONFIG: 'Configuración del Sistema',
    CONFIGURACION: 'Configuración del Sistema',
    USUARIOS: 'Usuarios y Roles',
    ROLES: 'Usuarios y Roles',
    USUARIOS_ROLES: 'Usuarios y Roles',
    SEGURIDAD: 'Usuarios y Roles',
};

const PERMISSION_DEPS: Record<string, string> = {
    'proyectos.crear': 'proyectos.ver',
    'proyectos.editar': 'proyectos.ver',
    'proyectos.eliminar': 'proyectos.ver',
    'unidades.editar': 'unidades.ver',
    'clientes.editar': 'clientes.ver',
    'precios.editar': 'precios.ver',
    'ventas.crear': 'ventas.ver',
    'proveedores.editar': 'proveedores.ver',
    'oc.crear': 'oc.ver',
    'oc.editar': 'oc.ver',
    'facturas_proveedor.editar': 'facturas_proveedor.ver',
    'cobranzas.operar': 'cobranzas.ver',
    'cajas.operar': 'cajas.ver',
    'contabilidad.operar': 'contabilidad.ver',
    'config.editar': 'config.ver',
    'roles.gestionar': 'roles.ver',
    'dashboard.finanzas': 'dashboard.ver',
    'dashboard.ventas': 'dashboard.ver',
    'dashboard.compras': 'dashboard.ver',
};

const ROLE_COLOR_MAP: Record<string, string> = {
    ADMIN: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
    VENTAS: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
    COMPRAS: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400',
    CONTABILIDAD: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
};

function getRoleColor(name: string) {
    return ROLE_COLOR_MAP[name] ?? 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300';
}

function formatRoleName(name: string): string {
    if (name === 'SIN_ASIGNAR') return 'Sin asignar';
    const lower = name.toLowerCase();
    return lower.charAt(0).toUpperCase() + lower.slice(1);
}

function getModuleDisplayName(module: string): string {
    const key = module.trim().toUpperCase();
    return MODULE_DISPLAY_MAP[key] ?? key.replace(/_/g, ' ');
}

function getPermissionDisplay(perm: Permission): PermissionDisplayConfig {
    return PERMISSION_DISPLAY_MAP[perm.code] ?? {
        title: perm.description,
        detail: 'Permite habilitar esta capacidad funcional dentro del módulo correspondiente.',
    };
}

// ── Component ──────────────────────────────────────────────────────────────

const RolesPage: React.FC = () => {
    const { isOwner, hasPermission } = useAuth();
    const canManageSecurity = isOwner() || hasPermission('roles.gestionar');

    const [loading, setLoading] = useState(true);
    const [roles, setRoles] = useState<RoleWithPermissions[]>([]);
    const [permissions, setPermissions] = useState<Permission[]>([]);
    const [view, setView] = useState<'list' | 'form'>('list');
    const [editingRole, setEditingRole] = useState<RoleWithPermissions | null>(null);
    const [roleForm, setRoleForm] = useState({ name: '', description: '', permissionIds: [] as number[] });
    const [deactivatingRoleId, setDeactivatingRoleId] = useState<number | null>(null);

    const fetchData = async () => {
        try {
            setLoading(true);
            const [rolesData, permsData] = await Promise.all([
                roleService.getAll(),
                roleService.getPermissions(),
            ]);
            setRoles(rolesData);
            setPermissions(permsData);
        } catch {
            showError('Error al cargar roles');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchData(); }, []);

    // ── Permission toggle (with dependency resolution) ─────────────────────

    const togglePermission = (permId: number) => {
        const perm = permissions.find(p => p.id === permId);
        if (!perm) return;
        setRoleForm(prev => {
            const selected = prev.permissionIds.includes(permId);
            let ids = [...prev.permissionIds];
            if (selected) {
                ids = ids.filter(id => id !== permId);
                const dependentCodes = Object.entries(PERMISSION_DEPS)
                    .filter(([, view]) => view === perm.code)
                    .map(([code]) => code);
                const dependentIds = permissions.filter(p => dependentCodes.includes(p.code)).map(p => p.id);
                ids = ids.filter(id => !dependentIds.includes(id));
            } else {
                ids.push(permId);
                const requiredCode = PERMISSION_DEPS[perm.code];
                if (requiredCode) {
                    const viewPerm = permissions.find(p => p.code === requiredCode);
                    if (viewPerm && !ids.includes(viewPerm.id)) ids.push(viewPerm.id);
                }
            }
            return { ...prev, permissionIds: ids };
        });
    };

    // ── Role handlers ──────────────────────────────────────────────────────

    const openCreate = () => {
        if (!canManageSecurity) { showError('Solo el administrador puede crear roles'); return; }
        setEditingRole(null);
        setRoleForm({ name: '', description: '', permissionIds: [] });
        setView('form');
    };

    const openEdit = (role: RoleWithPermissions) => {
        if (!canManageSecurity) { showError('Solo el administrador puede modificar roles'); return; }
        setEditingRole(role);
        setRoleForm({
            name: role.name,
            description: role.description,
            permissionIds: permissions
                .filter(p => role.permissions.some(rp => rp.code === p.code))
                .map(p => p.id),
        });
        setView('form');
    };

    const handleCreate = async () => {
        if (!roleForm.name || !roleForm.description) { showError('Nombre y descripción son obligatorios'); return; }
        try {
            await roleService.create({
                name: roleForm.name.toUpperCase(),
                description: roleForm.description,
                permissionIds: roleForm.permissionIds,
            });
            showSuccess('Rol creado exitosamente');
            setView('list');
            setRoleForm({ name: '', description: '', permissionIds: [] });
            fetchData();
        } catch (error: any) {
            showError(error?.response?.data?.message || 'Error al crear rol');
        }
    };

    const handleUpdate = async () => {
        if (!editingRole) return;
        try {
            await roleService.update(editingRole.id, {
                description: roleForm.description,
                permissionIds: roleForm.permissionIds,
            });
            showSuccess('Rol actualizado exitosamente');
            setEditingRole(null);
            setView('list');
            fetchData();
        } catch (error: any) {
            showError(error?.response?.data?.message || 'Error al actualizar rol');
        }
    };

    const handleDeactivateRole = async (role: RoleWithPermissions) => {
        if (role.isSystem) {
            showError(`El rol '${role.name}' es del sistema y no puede desactivarse`);
            return;
        }
        setDeactivatingRoleId(role.id);
        try {
            await roleService.deactivate(role.id);
            showSuccess(`Rol '${formatRoleName(role.name)}' desactivado. Los usuarios con este rol pasaron a Sin asignar.`);
            fetchData();
        } catch (error: any) {
            showError(error?.response?.data?.message || 'Error al desactivar el rol');
        } finally {
            setDeactivatingRoleId(null);
        }
    };

    const groupedPermissions = permissions.reduce((acc, perm) => {
        if (!acc[perm.module]) acc[perm.module] = [];
        acc[perm.module].push(perm);
        return acc;
    }, {} as Record<string, Permission[]>);

    if (loading) return <LoadingScreen message="Cargando roles" fullScreen={false} />;

    // ── List view ──────────────────────────────────────────────────────────

    if (view === 'list') {
        return (
            <div className="w-full h-full p-6 space-y-6">
                {/* Page header */}
                <div className="flex items-center gap-4">
                    <div className="flex items-center justify-center flex-shrink-0 bg-indigo-100 w-11 h-11 rounded-xl dark:bg-indigo-900/30">
                        <Key className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-foreground">Roles y Permisos</h1>
                        <p className="text-sm text-muted-foreground">Define los niveles de acceso y capacidades de cada rol.</p>
                    </div>
                </div>

                <div className="overflow-hidden border rounded-xl shadow-sm bg-card border-border">
                    {/* Header */}
                    <div className="flex items-center justify-between px-6 py-5 border-b border-border">
                        <p className="text-sm text-muted-foreground">
                            <span className="font-medium text-foreground">{roles.length}</span> {roles.length === 1 ? 'rol configurado' : 'roles configurados'}
                        </p>
                        {canManageSecurity && (
                            <Button size="sm" onClick={openCreate} className="flex items-center gap-2 rounded-lg">
                                <Plus className="w-4 h-4" />
                                Nuevo Rol
                            </Button>
                        )}
                    </div>

                    {/* Table */}
                    {roles.length === 0 ? (
                        <div className="py-16 text-center">
                            <Key className="w-10 h-10 mx-auto mb-3 text-muted-foreground/50" />
                            <p className="text-sm font-medium text-foreground mb-1">No hay roles</p>
                            <p className="text-xs text-muted-foreground">Comenzá creando el primer rol.</p>
                        </div>
                    ) : (
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-border bg-muted/40">
                                    <th className="px-5 py-3 text-xs font-semibold tracking-wide text-left text-muted-foreground uppercase">Rol</th>
                                    <th className="px-5 py-3 text-xs font-semibold tracking-wide text-left text-muted-foreground uppercase">Descripción</th>
                                    <th className="px-5 py-3 text-xs font-semibold tracking-wide text-left text-muted-foreground uppercase">Permisos</th>
                                    {canManageSecurity && (
                                        <th className="px-5 py-3 text-xs font-semibold tracking-wide text-center text-muted-foreground uppercase">Acciones</th>
                                    )}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-border">
                                {roles.map((role) => (
                                    <tr key={role.id} className="transition-colors hover:bg-muted/30 group">
                                        <td className="px-5 py-3.5">
                                            <div className="flex items-center gap-2">
                                                <span className={cn(
                                                    'inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium',
                                                    role.isActive === false
                                                        ? 'bg-muted text-muted-foreground line-through'
                                                        : getRoleColor(role.name),
                                                )}>
                                                    {formatRoleName(role.name)}
                                                </span>
                                                {role.isSystem && (
                                                    <span
                                                        title="Rol del sistema — permisos core no modificables"
                                                        className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-muted text-muted-foreground"
                                                    >
                                                        <Lock className="w-3 h-3" />
                                                    </span>
                                                )}
                                                {role.isActive === false && (
                                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 text-xs font-medium">
                                                        Inactivo
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-5 py-3.5 text-sm text-muted-foreground max-w-[300px]">
                                            {role.description}
                                        </td>
                                        <td className="px-5 py-3.5">
                                            {role.name === 'ADMIN' ? (
                                                <span className="text-xs font-medium text-purple-600 dark:text-purple-400">
                                                    Acceso Total
                                                </span>
                                            ) : role.isSystem ? (
                                                <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-muted text-xs text-muted-foreground font-medium">
                                                    {role.permissions.length} {role.permissions.length === 1 ? 'permiso' : 'permisos'}
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-muted text-xs text-muted-foreground font-medium">
                                                    {role.permissions.length} {role.permissions.length === 1 ? 'permiso' : 'permisos'}
                                                </span>
                                            )}
                                        </td>
                                        {canManageSecurity && (
                                            <td className="px-5 py-3.5 text-center">
                                                <DropdownMenu>
                                                    <DropdownMenuTrigger asChild>
                                                        <button
                                                            className="inline-flex items-center justify-center p-2 rounded-lg text-muted-foreground hover:bg-accent hover:text-foreground transition-colors opacity-0 group-hover:opacity-100 focus:opacity-100"
                                                            title="Más acciones"
                                                        >
                                                            <MoreVertical className="w-4 h-4" />
                                                        </button>
                                                    </DropdownMenuTrigger>
                                                    <DropdownMenuContent align="end" className="w-48">
                                                        <DropdownMenuItem onClick={() => openEdit(role)} className="cursor-pointer">
                                                            <Pencil className="w-4 h-4 mr-2" />
                                                            {role.isSystem ? 'Ver detalles' : 'Configurar'}
                                                        </DropdownMenuItem>
                                                        {/* Desactivar: solo roles custom activos (no sistema) */}
                                                        {role.isActive !== false && (
                                                            <DropdownMenuItem
                                                                onClick={() => !role.isSystem && handleDeactivateRole(role)}
                                                                disabled={role.isSystem || deactivatingRoleId === role.id}
                                                                className={cn(
                                                                    'cursor-pointer',
                                                                    role.isSystem
                                                                        ? 'opacity-40 cursor-not-allowed text-muted-foreground'
                                                                        : 'text-red-600 dark:text-red-400 focus:text-red-600 focus:bg-red-50 dark:focus:bg-red-900/20',
                                                                )}
                                                                title={role.isSystem ? 'Los roles del sistema no pueden desactivarse' : undefined}
                                                            >
                                                                <PowerOff className="w-4 h-4 mr-2" />
                                                                {deactivatingRoleId === role.id ? 'Desactivando...' : 'Desactivar'}
                                                            </DropdownMenuItem>
                                                        )}
                                                    </DropdownMenuContent>
                                                </DropdownMenu>
                                            </td>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}

                    {roles.length > 0 && (
                        <div className="px-6 py-4 border-t border-border">
                            <p className="text-xs text-muted-foreground">
                                Los roles marcados con <Lock className="inline w-3 h-3 mx-0.5 mb-0.5" /> son del sistema y no pueden eliminarse.
                            </p>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    // ── Form view ──────────────────────────────────────────────────────────

    return (
        <div className="w-full h-full p-6 space-y-6">
            {/* Back header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => { setView('list'); setEditingRole(null); }}
                        className="flex items-center gap-2 rounded-lg"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        Volver
                    </Button>
                    <div>
                        <h1 className="text-xl font-bold text-foreground">
                            {editingRole ? (editingRole.isSystem ? `Ver rol: ${formatRoleName(editingRole.name)}` : `Configurar: ${formatRoleName(editingRole.name)}`) : 'Nuevo Rol'}
                        </h1>
                        <p className="text-sm text-muted-foreground">
                            {editingRole?.isSystem
                                ? 'Rol del sistema: solo puede visualizarse, no admite modificaciones.'
                                : 'Define las capacidades y accesos para este nivel de usuario.'}
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={() => { setView('list'); setEditingRole(null); }}>
                        {editingRole?.isSystem ? 'Volver' : 'Cancelar'}
                    </Button>
                    {!editingRole?.isSystem && (
                        <Button size="sm" onClick={editingRole ? handleUpdate : handleCreate}>
                            {editingRole ? 'Guardar Cambios' : 'Crear Rol'}
                        </Button>
                    )}
                </div>
            </div>

            <div className="overflow-hidden border rounded-xl shadow-sm bg-card border-border">
                <div className="p-6 space-y-8">
                    {/* Identity fields */}
                    <div className="grid max-w-4xl grid-cols-1 gap-6 md:grid-cols-2">
                        {!editingRole && (
                            <div className="space-y-2">
                                <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                                    Identificador del Rol
                                </label>
                                <Input
                                    value={roleForm.name}
                                    onChange={(e) => setRoleForm({ ...roleForm, name: e.target.value.toUpperCase() })}
                                    className="uppercase rounded-lg"
                                    placeholder="Ej: AUDITORIA, RRHH"
                                />
                                <p className="text-xs text-muted-foreground">Se convierte automáticamente a MAYÚSCULAS.</p>
                            </div>
                        )}
                        <div className={cn('space-y-2', editingRole ? 'md:col-span-2' : '')}>
                            <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                                Descripción
                            </label>
                            <Input
                                value={roleForm.description}
                                onChange={(e) => setRoleForm({ ...roleForm, description: e.target.value })}
                                className="rounded-lg"
                                placeholder="Ej: Tiene acceso total a los reportes contables"
                                disabled={editingRole?.isSystem}
                                readOnly={editingRole?.isSystem}
                            />
                        </div>
                    </div>

                    {/* System role notice */}
                    {editingRole?.isSystem && (
                        <div className="flex items-start max-w-4xl gap-3 p-4 border border-indigo-200 rounded-xl bg-indigo-50 dark:bg-indigo-900/20 dark:border-indigo-800">
                            <Shield className="w-5 h-5 text-indigo-600 dark:text-indigo-400 mt-0.5 shrink-0" />
                            <div>
                                <h4 className="text-sm font-semibold text-indigo-900 dark:text-indigo-300">
                                    {editingRole?.name === 'ADMIN' ? 'Rol Administrador Maestro' : 'Rol del Sistema'}
                                </h4>
                                <p className="mt-1 text-xs text-indigo-700 dark:text-indigo-400">
                                    Este es un rol del sistema. Su código, descripción y permisos son fijos por diseño y no pueden modificarse por nadie, ni siquiera por el administrador. Solo puede visualizarse.
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Permissions grid — read-only for system roles: visible so admins can confirm
                        how they come configured, but not editable by anyone. */}
                    {canManageSecurity && (
                        <div className="space-y-6 max-w-4xl">
                            <h3 className="pb-2 text-sm font-semibold border-b text-foreground border-border">
                                Permisos de Acceso
                                {editingRole?.isSystem && (
                                    <span className="ml-2 text-xs font-normal normal-case text-muted-foreground">(solo lectura)</span>
                                )}
                            </h3>
                            <div className="space-y-8">
                                {Object.entries(groupedPermissions).map(([moduleName, perms]) => (
                                    <div key={moduleName}>
                                        <h4 className="mb-4 text-xs font-bold tracking-widest uppercase text-indigo-600 dark:text-indigo-400 border-b border-indigo-100 dark:border-indigo-900 pb-2">
                                            {getModuleDisplayName(moduleName)}
                                        </h4>
                                        <div className="pl-2 sm:pl-4 space-y-4">
                                            {perms
                                                .filter(p => !PERMISSION_DEPS[p.code])
                                                .map(parentPerm => {
                                                    const parentChecked = roleForm.permissionIds.includes(parentPerm.id);
                                                    const children = perms.filter(p => PERMISSION_DEPS[p.code] === parentPerm.code);
                                                    const parentDisplay = getPermissionDisplay(parentPerm);

                                                    const readOnly = !!editingRole?.isSystem;

                                                    return (
                                                        <div key={parentPerm.id} className="space-y-2">
                                                            <label className={cn('flex items-start gap-3 w-fit group', readOnly ? 'cursor-default' : 'cursor-pointer')}>
                                                                <div className="relative flex items-center mt-0.5">
                                                                    <input
                                                                        type="checkbox"
                                                                        className="sr-only peer"
                                                                        checked={parentChecked}
                                                                        disabled={readOnly}
                                                                        onChange={() => !readOnly && togglePermission(parentPerm.id)}
                                                                    />
                                                                    <div className="flex items-center justify-center w-4 h-4 border rounded shadow-sm border-input bg-background transition-colors peer-checked:bg-primary peer-checked:border-primary peer-disabled:opacity-70">
                                                                        {parentChecked && <Check className="w-3 h-3 text-primary-foreground stroke-[3]" />}
                                                                    </div>
                                                                </div>
                                                                <div>
                                                                    <p className="text-sm font-medium text-foreground leading-snug">{parentDisplay.title}</p>
                                                                    <p className="text-xs text-muted-foreground mt-0.5">{parentDisplay.detail}</p>
                                                                </div>
                                                            </label>

                                                            {children.length > 0 && (
                                                                <div className="pl-7 mt-2">
                                                                    <div className="pl-4 space-y-3 border-l-2 border-border/60 py-1">
                                                                        {children.map(childPerm => {
                                                                            const childChecked = roleForm.permissionIds.includes(childPerm.id);
                                                                            const childDisplay = getPermissionDisplay(childPerm);
                                                                            return (
                                                                                <label key={childPerm.id} className={cn('flex items-start gap-3 w-fit group', readOnly ? 'cursor-default' : 'cursor-pointer')}>
                                                                                    <div className="relative flex items-center mt-0.5">
                                                                                        <input
                                                                                            type="checkbox"
                                                                                            className="sr-only peer"
                                                                                            checked={childChecked}
                                                                                            disabled={readOnly}
                                                                                            onChange={() => !readOnly && togglePermission(childPerm.id)}
                                                                                        />
                                                                                        <div className="flex items-center justify-center w-4 h-4 border rounded shadow-sm border-input bg-background transition-colors peer-checked:bg-primary peer-checked:border-primary peer-disabled:opacity-70">
                                                                                            {childChecked && <Check className="w-3 h-3 text-primary-foreground stroke-[3]" />}
                                                                                        </div>
                                                                                    </div>
                                                                                    <div>
                                                                                        <p className="text-sm font-medium text-muted-foreground leading-snug">{childDisplay.title}</p>
                                                                                        <p className="text-xs text-muted-foreground/70 mt-0.5">{childDisplay.detail}</p>
                                                                                    </div>
                                                                                </label>
                                                                            );
                                                                        })}
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default RolesPage;
